import { ButtonTertiary } from "@nova/component-btn"
import { FirePerimeterMethod } from "@nova/constants"
import { ReactElement, useMemo } from "react"
import * as Icons from "@nova/component-icons"
import { FirePerimeterPluginActionMethodUpload } from "./methods/upload/FirePerimeterPluginActionMethodUpload"
import { FirePerimeterPluginActionMethodManual } from "./methods/manual/FirePerimeterPluginActionMethodManual"
import { SidePanelPluginActionsNavigator } from "panels/content/side-panel/plugin-actions/SidePanelPluginActionsNavigator"
import { SidePanelPluginActionsRenamer } from "panels/content/side-panel/plugin-actions/SidePanelPluginActionsRenamer"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setActiveTool } from "plugins/Toolbox/tool-registry"
import { FirePerimeterPluginActionMethodAuto } from "./methods/auto/FirePerimeterPluginActionMethodAuto"
import { selectNextFirePerimeterNumber } from "features/entity-caching/fire-perimeters/selectors"
import {
    changeUpdateMethod,
    resetFirePerimeterPreview,
    selectNextFirePerimeterName,
    selectUpdateMethod,
    setMockedFirePerimeterName,
} from "../FirePerimeterSlice"
import { setActiveFeatureId, setActiveLayerId } from "panels/content/side-panel/slice.new"

export const FirePerimeterPluginActionBody = () => {
    const dispatch = useAppDispatch()
    const updateMethod = useAppSelector(selectUpdateMethod())
    const nextFirePerimeterName = "Fire Perimeter " + useAppSelector(selectNextFirePerimeterNumber)
    const layerName = useAppSelector(selectNextFirePerimeterName)

    const Body = useMemo(() => {
        let Body: (({ onBlur }: { onBlur: () => void }) => ReactElement) | undefined
        switch (updateMethod) {
            case FirePerimeterMethod.Upload:
                Body = FirePerimeterPluginActionMethodUpload
                break
            case FirePerimeterMethod.Manual:
                Body = FirePerimeterPluginActionMethodManual
                break
            case FirePerimeterMethod.Auto:
                Body = FirePerimeterPluginActionMethodAuto
                break
        }
        return Body
    }, [updateMethod])

    const backButton = useMemo(() => {
        switch (updateMethod) {
            case FirePerimeterMethod.None:
                return {
                    backTitle: "Cancel",
                    onBack: () => {
                        dispatch(setActiveFeatureId({ feature: undefined }))
                        dispatch(setActiveLayerId({ id: undefined }))
                    },
                }
            default:
                return {
                    backTitle: "Cancel",
                    onBack: () => {
                        dispatch(changeUpdateMethod({ method: FirePerimeterMethod.None }))

                        // De-select fire perimeter tool.
                        dispatch(setActiveTool(undefined))

                        // Reset preview.
                        dispatch(resetFirePerimeterPreview())
                    },
                }
        }
    }, [updateMethod, dispatch])

    return (
        <SidePanelPluginActionsNavigator {...backButton} layerName={layerName}>
            {/* Show layer rename field only while a method hasn't been selected yet. */}
            {updateMethod === FirePerimeterMethod.None && (
                <SidePanelPluginActionsRenamer
                    defaultName={nextFirePerimeterName}
                    onRename={(name) => dispatch(setMockedFirePerimeterName(name))}
                />
            )}
            <div className="mt-2 flex flex-col min-h-0">
                {!Body ? (
                    <>
                        <h5 className="text-gray-0 select-none">Select a method</h5>
                        <div className="flex flex-col gap-1 mt-2">
                            <ButtonTertiary
                                key="upload"
                                title="Upload"
                                LeftIcon={Icons.Upload}
                                height="2.5rem"
                                onClick={() =>
                                    dispatch(
                                        changeUpdateMethod({ method: FirePerimeterMethod.Upload })
                                    )
                                }
                                clickWillNavigate
                            />
                            <ButtonTertiary
                                key="manual"
                                title="Draw"
                                LeftIcon={Icons.StylusNote}
                                height="2.5rem"
                                onClick={() =>
                                    dispatch(
                                        changeUpdateMethod({ method: FirePerimeterMethod.Manual })
                                    )
                                }
                                clickWillNavigate
                            />
                            <ButtonTertiary
                                key="auto"
                                title="Auto"
                                LeftIcon={Icons.GestureSelect}
                                height="2.5rem"
                                onClick={() =>
                                    dispatch(
                                        changeUpdateMethod({ method: FirePerimeterMethod.Auto })
                                    )
                                }
                                clickWillNavigate
                            />
                        </div>
                    </>
                ) : (
                    <Body
                        onBlur={() => {
                            dispatch(resetFirePerimeterPreview())
                            dispatch(setActiveFeatureId({ feature: undefined }))
                            dispatch(setActiveLayerId({ id: undefined }))
                        }}
                    />
                )}
            </div>
        </SidePanelPluginActionsNavigator>
    )
}
